import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaHeartbeat, FaDumbbell, FaAppleAlt, FaMoon, FaBrain,
  FaCheckCircle, FaChartLine, FaComments, FaCalendarAlt,
  FaMobileAlt, FaUsers, FaClipboardList, FaLeaf, FaBed,
  FaFire, FaSmile, FaTrophy, FaArrowRight
} from 'react-icons/fa';
import images from '../assets/images';
import SEO, { PageSEO } from '../components/SEO';

const OurProgram = () => {
  const [selectedTab, setSelectedTab] = useState('overview');

  return (
    <div className="min-h-screen bg-white">
      <SEO {...PageSEO.ourProgram} />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={images.hero.program}
            alt="Program"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <FaLeaf className="text-success-400" />
              <span className="text-sm font-medium">Natural & Science-Backed Weight Loss</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Transform Your Body the Natural Way — For Life
            </h1>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              A complete holistic program — personalized nutrition, guided workouts, expert 1:1 coaching, and habit-building support — designed to help you lose weight and keep it off.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link
                to="/pricing"
                className="w-full sm:w-auto bg-white text-primary-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all shadow-xl"
              >
                View Programs
              </Link>
              <Link
                to="/register"
                className="w-full sm:w-auto bg-primary-500 border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-primary-400 transition-all"
              >
                Get Started Today
              </Link>
            </div>
            <p className="mt-6 text-sm text-primary-200">
              ✓ No hidden costs &nbsp; ✓ Personalised plans &nbsp; ✓ Expert coaches
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 0L60 10C120 20 240 40 360 46.7C480 53 600 47 720 43.3C840 40 960 40 1080 46.7C1200 53 1320 67 1380 73.3L1440 80V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0V0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything Included. One Simple Price.
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All the coaching, nutrition plans, fitness guidance, and lifestyle support you need for sustainable, lasting weight loss — no gimmicks, no shortcuts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Custom Meal Plans */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-primary-200 overflow-hidden">
              <div className="h-40 relative">
                <img src={images.food.mealPrep} alt="Custom Meal Plans" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
                    <FaAppleAlt className="text-xl text-white" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Custom Meal Plans</h3>
                <p className="text-gray-600 mb-4">
                  Personalized nutrition plans crafted around your food preferences, lifestyle, and goals — no crash diets.
                </p>
                <ul className="space-y-3">
                  {['Indian & regional cuisine friendly', 'Calorie & macro tracking', 'Easy-to-follow weekly plans', 'Flexible dietary preferences'].map(f => (
                    <li key={f} className="flex items-start">
                      <FaCheckCircle className="text-success-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Workout Plans */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-primary-200 overflow-hidden">
              <div className="h-40 relative">
                <img src={images.fitness.gym} alt="Workout Plans" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-success-500 rounded-xl flex items-center justify-center">
                    <FaDumbbell className="text-xl text-white" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Workout Plans</h3>
                <p className="text-gray-600 mb-4">
                  Custom exercise routines designed for your fitness level — whether you prefer home workouts or the gym.
                </p>
                <ul className="space-y-3">
                  {['Beginner to advanced levels', 'Home & gym options', 'Progressive difficulty', 'Video exercise library'].map(f => (
                    <li key={f} className="flex items-start">
                      <FaCheckCircle className="text-success-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* 1:1 Expert Coaching */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-primary-200 overflow-hidden">
              <div className="h-40 relative">
                <img src={images.features.coaching} alt="Personal Coaching" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center">
                    <FaComments className="text-xl text-white" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">1:1 Expert Coaching</h3>
                <p className="text-gray-600 mb-4">
                  A dedicated certified coach who keeps you accountable, motivated, and on track every step of the way.
                </p>
                <ul className="space-y-3">
                  {['Weekly coaching sessions', 'Unlimited chat support', 'Goal setting & tracking', 'Behavioral change strategies'].map(f => (
                    <li key={f} className="flex items-start">
                      <FaCheckCircle className="text-success-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sleep & Recovery */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-primary-200 overflow-hidden">
              <div className="h-40 relative">
                <img src="https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?w=600&q=80" alt="Sleep & Recovery" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center">
                    <FaBed className="text-xl text-white" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Sleep & Recovery</h3>
                <p className="text-gray-600 mb-4">
                  Optimizing sleep is one of the most powerful natural levers for weight loss — we make it part of your plan.
                </p>
                <ul className="space-y-3">
                  {['Sleep quality assessment', 'Evening routine guidance', 'Stress & cortisol management', 'Recovery nutrition tips'].map(f => (
                    <li key={f} className="flex items-start">
                      <FaCheckCircle className="text-success-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Mental Wellness */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-primary-200 overflow-hidden">
              <div className="h-40 relative">
                <img src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=600&q=80" alt="Mental Wellness" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-success-500 rounded-xl flex items-center justify-center">
                    <FaBrain className="text-xl text-white" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Mental Wellness</h3>
                <p className="text-gray-600 mb-4">
                  Break free from emotional eating and build a positive, lasting relationship with food and your body.
                </p>
                <ul className="space-y-3">
                  {['Mindful eating practices', 'Emotional eating support', 'Stress management tools', 'Positive habit formation'].map(f => (
                    <li key={f} className="flex items-start">
                      <FaCheckCircle className="text-success-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Digital Platform */}
            <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-primary-200 overflow-hidden">
              <div className="h-40 relative">
                <img src={images.features.technology} alt="Digital Platform" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4">
                  <div className="w-12 h-12 bg-accent-500 rounded-xl flex items-center justify-center">
                    <FaMobileAlt className="text-xl text-white" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Progress Dashboard</h3>
                <p className="text-gray-600 mb-4">
                  Track everything in one place — weight, workouts, nutrition, check-ins, and milestones.
                </p>
                <ul className="space-y-3">
                  {['Progress tracking dashboard', 'Weekly check-in reports', 'Educational content library', '24/7 access from any device'].map(f => (
                    <li key={f} className="flex items-start">
                      <FaCheckCircle className="text-success-600 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Six Pillars Methodology */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Six-Pillar Holistic Approach
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              True, lasting transformation comes from addressing every dimension of health — not just what you eat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <PillarCard
              icon={FaAppleAlt}
              title="Nutrition"
              description="Science-backed dietary guidance tailored to your preferences, culture, and goals — helping you build sustainable eating habits for life."
              color="primary"
            />
            <PillarCard
              icon={FaDumbbell}
              title="Exercise"
              description="Personalized movement plans that fit your lifestyle and fitness level, designed to boost metabolism and build lasting strength."
              color="success"
            />
            <PillarCard
              icon={FaComments}
              title="1:1 Coaching"
              description="Expert coaches provide accountability, motivation, and personalized guidance every week, so you never feel alone on your journey."
              color="accent"
            />
            <PillarCard
              icon={FaMoon}
              title="Sleep & Recovery"
              description="Optimize your sleep to regulate hunger hormones, improve metabolism, and accelerate fat loss naturally — without any shortcuts."
              color="primary"
            />
            <PillarCard
              icon={FaBrain}
              title="Mental Health"
              description="Address emotional eating, stress, and mindset barriers so your transformation is as much psychological as it is physical."
              color="success"
            />
            <PillarCard
              icon={FaHeartbeat}
              title="Lifestyle"
              description="Create lasting behavioral changes through daily habit formation, stress management, and a sustainable routine that works for your life."
              color="accent"
            />
          </div>
        </div>
      </section>

      {/* How It Works Timeline */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Your Journey with Fitnession
            </h2>
            <p className="text-xl text-gray-600">
              From sign-up to transformation — here's exactly what to expect
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-primary-200"></div>
            <div className="space-y-12">
              <TimelineItem
                step="Day 1"
                title="Health Assessment & Goal Setting"
                description="Complete a detailed health profile. Tell us about your lifestyle, food preferences, fitness level, and goals — we use this to build your 100% personalized plan."
                icon={FaClipboardList}
                side="left"
              />
              <TimelineItem
                step="Day 2–3"
                title="Meet Your Dedicated Coach"
                description="Have a one-on-one introduction call with your certified health coach. Together you'll set clear, realistic goals and align on your action plan."
                icon={FaUsers}
                side="right"
              />
              <TimelineItem
                step="Day 4–5"
                title="Your Personalized Plan is Ready"
                description="Receive your custom meal plan, workout schedule, and daily habit guide — all tailored to your body type, lifestyle, and preferences."
                icon={FaLeaf}
                side="left"
              />
              <TimelineItem
                step="Week 1"
                title="Begin Your Transformation"
                description="Start following your nutrition and workout plan with full coach support. Track meals, workouts, water intake, and sleep on your dashboard."
                icon={FaFire}
                side="right"
              />
              <TimelineItem
                step="Weeks 2–4"
                title="Build Momentum"
                description="Weekly check-ins with your coach to review progress, adjust your plan if needed, and celebrate wins. Most members notice real changes by week 3."
                icon={FaChartLine}
                side="left"
              />
              <TimelineItem
                step="Month 2+"
                title="Sustained Results"
                description="Continuous coaching, evolving workout plans, and ongoing habit-building ensure the results stick — not just for months, but for life."
                icon={FaTrophy}
                side="right"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Results Proof */}
      <section className="py-16 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { number: '1,000+', label: 'Members Transformed' },
              { number: '18%', label: 'Avg. Body Weight Lost' },
              { number: '94%', label: 'Satisfaction Rate' },
              { number: '85%', label: 'Maintain Results Long-Term' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-primary-200 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            <FAQItem
              question="What makes Fitnession different from other programs?"
              answer="We take a 100% natural, holistic approach — personalized meal plans, custom workout programs, and dedicated 1:1 coaching. No shortcuts, no pills. We address nutrition, fitness, sleep, mindset, and lifestyle together, which is why our results last."
            />
            <FAQItem
              question="How quickly will I see results?"
              answer="Most members see visible progress within the first 3–4 weeks. On average, members following the complete program lose 8–15% of their body weight over 3 months while building habits that sustain those results long-term."
            />
            <FAQItem
              question="Do I need to follow a strict diet?"
              answer="Not at all. We create meal plans around your actual food preferences — Indian cuisine, regional dishes, dietary restrictions — and gradually improve your eating habits. It's about building sustainable patterns, not suffering through bland food."
            />
            <FAQItem
              question="Are the coaches qualified?"
              answer="Yes. All our coaches are certified nutrition and fitness coaches with extensive training in behavioral change, habit psychology, and sustainable weight loss. For the Medical Care plan, licensed doctors also monitor health conditions."
            />
            <FAQItem
              question="What if I have dietary restrictions or preferences?"
              answer="Our meal plans are fully customizable — vegetarian, vegan, Jain, gluten-free, regional cuisines, food allergies. Your coach will build a plan around your lifestyle, not the other way around."
            />
            <FAQItem
              question="Do I need a gym membership?"
              answer="No. We design workout plans that work at home with zero equipment, or at the gym if you prefer. All plans are tailored to your environment, fitness level, and how much time you have each day."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your Natural Transformation?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands who have permanently transformed their health through personalized nutrition, guided fitness, and expert coaching — the natural way.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link
              to="/pricing"
              className="w-full sm:w-auto bg-white text-primary-700 px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all shadow-xl inline-flex items-center justify-center"
            >
              View Programs
              <FaArrowRight className="ml-2" />
            </Link>
            <Link
              to="/register"
              className="w-full sm:w-auto bg-primary-500 border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-primary-400 transition-all inline-flex items-center justify-center"
            >
              Get Started Free
              <FaArrowRight className="ml-2" />
            </Link>
          </div>
          <p className="mt-6 text-primary-200 text-sm">
            Questions? Email us at support@fitnession.fit or call +91 9579520586
          </p>
        </div>
      </section>
    </div>
  );
};

const PillarCard = ({ icon: Icon, title, description, color }) => {
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    success: 'bg-success-100 text-success-600',
    accent: 'bg-accent-100 text-accent-600',
  };
  return (
    <div className="bg-white rounded-xl p-6 border-2 border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all">
      <div className={`w-14 h-14 ${colorClasses[color].split(' ')[0]} rounded-lg flex items-center justify-center mb-4`}>
        <Icon className={`text-2xl ${colorClasses[color].split(' ')[1]}`} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const TimelineItem = ({ step, title, description, icon: Icon, side }) => (
  <div className={`relative flex items-center ${side === 'right' ? 'lg:flex-row-reverse' : ''}`}>
    <div className={`lg:w-5/12 ${side === 'right' ? 'lg:text-right lg:pr-12' : 'lg:pl-12'}`}>
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <span className="text-primary-600 font-bold text-lg">{step}</span>
        <h3 className="text-xl font-bold text-gray-900 mt-1 mb-2">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
    <div className="hidden lg:flex absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-primary-600 rounded-full items-center justify-center shadow-lg z-10">
      <Icon className="text-white text-xl" />
    </div>
    <div className="hidden lg:block lg:w-5/12"></div>
  </div>
);

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 pr-4">{question}</span>
        <svg
          className={`w-5 h-5 text-primary-600 transform transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-5">
          <p className="text-gray-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

export default OurProgram;
